import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { TextStyle } from '@tiptap/extension-text-style'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import React from 'react'
import './RichTextEditor.css'

interface RichTextEditorProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    height?: string | number
    minHeight?: number
    modules?: any
    formats?: string[]
    className?: string
    readOnly?: boolean
    disabled?: boolean
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = '',
    height = '',
    minHeight,
    className = '',
    readOnly = false,
    disabled = false,
}) => {
    const isDisabled = disabled || readOnly

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
            }),
            Image,
            TextStyle,
        ],
        content: value,
        editable: !isDisabled,
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML())
        },
        editorProps: {
            attributes: {
                class: `tiptap-editor ${isDisabled ? 'disabled' : ''}`,
                style: minHeight ? `min-height: ${minHeight}px` : undefined,
                'data-placeholder': placeholder,
            },
        },
    })

    React.useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value)
        }
    }, [value, editor])

    if (!editor) {
        return null
    }

    return (
        <div className={className} style={{ height }}>
            {!isDisabled && (
                <div className="tiptap-toolbar">
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={editor.isActive('bold') ? 'is-active' : ''}
                    >
                        <strong>B</strong>
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={editor.isActive('italic') ? 'is-active' : ''}
                    >
                        <em>I</em>
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        className={editor.isActive('strike') ? 'is-active' : ''}
                    >
                        <s>S</s>
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        className={editor.isActive('code') ? 'is-active' : ''}
                    >
                        &lt;/&gt;
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
                    >
                        H1
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
                    >
                        H2
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={editor.isActive('bulletList') ? 'is-active' : ''}
                    >
                        • List
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={editor.isActive('orderedList') ? 'is-active' : ''}
                    >
                        1. List
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        className={editor.isActive('blockquote') ? 'is-active' : ''}
                    >
                        "
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const url = window.prompt('Enter link URL:')
                            if (url) {
                                editor.chain().focus().setLink({ href: url }).run()
                            }
                        }}
                        className={editor.isActive('link') ? 'is-active' : ''}
                    >
                        Link
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const url = window.prompt('Enter image URL:')
                            if (url) {
                                editor.chain().focus().setImage({ src: url }).run()
                            }
                        }}
                    >
                        Image
                    </button>
                    <button
                        type="button"
                        onClick={() => editor.chain().focus().unsetLink().run()}
                        disabled={!editor.isActive('link')}
                    >
                        Unlink
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().unsetAllMarks().run()}>
                        Clear marks
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().clearNodes().run()}>
                        Clear nodes
                    </button>
                </div>
            )}
            <EditorContent editor={editor} />
        </div>
    )
}

export default RichTextEditor
